import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import {
  AppBar,
  Box,
  CircularProgress,
  ClickAwayListener,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import CustomPrompt from "../CustomPrompt/CustomPrompt";
import CustomNewModal from "../CustomNewModal/CustomNewModal";
import HelpMenu from "../HelpMenu/HelpMenu";
import HeaderActionDropDown from "./HeaderActionDropDown";
import HeaderItem from "./HeaderItem";
import InitialsImage from "../InitialsImage/InitialsImage";
import LogoNova from "../../img/carePulseLogo.png";
import Chatbot from "../../img/Chatbot.svg";
import ActiveChatbot from "../../img/ActiveChatbot.svg";
import warningIcon from "../../img/warningIcon.svg";
import composeEmail from "../../utils/composeEmail";
import removeToken from "../../Auth/removeToken";
import { useWindowDimension } from "../../CustomHooks/useWindowDimension";
import { removeUserSignUpInformationFromLocalStorage } from "../../containers/utils/removeUserSignupInformationFromLocalStoarge";
import {
  ACTION_DROPDOWN_ITEMS,
  HEADER_ITEMS,
  NON_LOGGED_IN_DROPDOWN,
} from "../../consts/accountActionDropDown";
import {
  ALL_APPOINTMENTS,
  DASHBOARD,
  FAVORITES,
  HIPPA_ROUTE,
  LIST_PRACTICE,
  LOGIN,
  PRIVACY_POLICY_ROUTE,
  SIGNUP,
  SPLASH,
  TERMS_AND_CONDITION,
} from "../../Auth/Routes";
import { teamCarePulseEmail } from "../../consts/constant";
import { styles } from "./HeaderStyles";
import { LanguageSelector } from "../LanguageSelector/LanguageSelector";
import LoggedInLanguagePopup from "../LanguagePreferencePopup/LoggedInLanguagePopup";
import { languageArray } from "../LanguageSelector/LanguageSelectorConstant";
import menuIcon from "../../img/menuIcon.png";

const Header = () => {
  const [showActionDropDown, setShowActionDropDown] = useState(false);
  const [showLanguagePreferencePopup, setShowLanguagePreferencePopup] =
    useState(false);
  const [customModalDetails, setCustomModalDetails] = useState({});
  const [anchorHelpPopover, setAnchorHelpPopover] = useState(null);
  const [openBrochure, setOpenBrochure] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const [width] = useWindowDimension();
  const onlyIncludeIcon = [
    TERMS_AND_CONDITION,
    HIPPA_ROUTE,
    PRIVACY_POLICY_ROUTE,
  ].some(
    (route) => location.pathname === route || location.pathname.endsWith(route),
  );

  const { firstName, profileImage, email, country, city, state } = useSelector(
    (state) => {
      return state?.userProfile?.currentUserData?.data || {};
    },
  );
  const { isLoggingOutUser, isLoginApiSuccess } = useSelector(
    (reduxState) => reduxState.login,
  );
  const language = useSelector((state) => state.languageChange.language);
  const selectedFlag =
    languageArray.find((item) => item.key === language) || languageArray[0];

  const redirectToHome = () => {
    if (location.pathname !== DASHBOARD) {
      history.push(DASHBOARD, { scrollToTop: true });
    }
  };

  const redirectToDefaultPage = () => {
    if (location.pathname !== SPLASH) {
      if (location.pathname === SIGNUP) {
        removeUserSignUpInformationFromLocalStorage();
      }
      history.push(SPLASH);
    }
  };

  const toggleActionDropDown = () => {
    setShowActionDropDown((prev) => !prev);
  };

  const closeActionDropdown = () => {
    setShowActionDropDown(false);
  };

  const redirectToPage = (path) => {
    if (location.pathname !== path) {
      history.push(path);
    }
  };

  const headerNonLoggedInActions = useCallback(() => {
    if (location.pathname === LIST_PRACTICE) {
      return {
        showheaderDetails: true,
        headingText: "",
        buttonText: "",
        buttonIcon: "homeIcon",
        buttonClickHandler: () => {
          redirectToPage(SPLASH);
        },
      };
    }
    return {
      showheaderDetails: true,
      headingText: t("CARE_PULSE_PRACTICE_LIST"),
      onHeadingClick: () => {
        redirectToPage(LIST_PRACTICE);
      },
      buttonText: "",
      buttonClickHandler: () => {},
    };
  }, [location.pathname]);

  const headerItemClick = (path) => {
    if (location.pathname === path) {
      return;
    }
    history.push(path);
  };

  const { t, i18n } = useTranslation();

  return (
    <React.Fragment>
      <AppBar position="sticky">
        {isLoginApiSuccess ? (
          <Box sx={styles.appBarContainer} maxWidth="xl">
            <Toolbar>
              <Typography
                component="img"
                sx={styles.logoStyles}
                src={LogoNova}
                alt="Novadontics"
                onClick={redirectToHome}
                width={100}
                height={50}
              />
            </Toolbar>
            {!onlyIncludeIcon && (
              <Box sx={styles.otherOptionsContainer}>
                <Box
                  sx={styles.fieldContainer}
                  onClick={() => setShowLanguagePreferencePopup(true)}
                >
                  <Typography
                    component="img"
                    src={selectedFlag.flagImage}
                    alt={`${selectedFlag.label} flag`}
                    sx={styles.iconStyles}
                  />
                </Box>
                {width > 900 && (
                  <>
                    {HEADER_ITEMS.map((item, idx) => (
                      <HeaderItem
                        key={idx}
                        headerTitle={t(item.headerTitle)}
                        onClick={() => headerItemClick(item.redirectPath)}
                        largerText={item.largerText}
                        isSelected={location.pathname === item.redirectPath}
                        isImageView={item.redirectPath === ALL_APPOINTMENTS}
                        imgSrc={
                          item.redirectPath === ALL_APPOINTMENTS ? (
                            Chatbot
                          ) : (
                            <></>
                          )
                        }
                        selectedImgSrc={
                          item.redirectPath === ALL_APPOINTMENTS ? (
                            ActiveChatbot
                          ) : (
                            <></>
                          )
                        }
                        MuiIcon={
                          item.redirectPath === FAVORITES ? (
                            FavoriteBorderIcon
                          ) : (
                            <></>
                          )
                        }
                      />
                    ))}
                  </>
                )}
                <Box style={styles.container}>
                  <ClickAwayListener onClickAway={closeActionDropdown}>
                    <Box>
                      <Box sx={styles.profileMenuContainer}>
                        {profileImage ? (
                          <Typography
                            component="img"
                            src={profileImage}
                            alt="profile-img"
                            sx={styles.profileImageStyle}
                          />
                        ) : firstName ? (
                          <Box sx={styles.defaultImageContainer}>
                            <InitialsImage
                              initials={(firstName || "").charAt(0)}
                              initialsImageCustomStyle={
                                styles.initialsImageCustomStyle
                              }
                              initialsTextCustomStyle={
                                styles.initialsTextCustomStyle
                              }
                            />
                          </Box>
                        ) : null}
                        <Box>
                          <Typography sx={styles.greetingText}>
                            {t("HELLO!")}
                          </Typography>
                          <Typography sx={styles.profileText}>
                            {firstName}
                          </Typography>
                        </Box>
                        <Box onClick={toggleActionDropDown} sx={styles.pointer}>
                          <Typography
                            sx={{ height: "1.5rem", widht: "1.5rem" }}
                            component="img"
                            src={menuIcon}
                            height={24}
                            width={24}
                            alt="Open profile menu"
                          ></Typography>
                          &nbsp;
                        </Box>
                      </Box>
                      {showActionDropDown && (
                        <HeaderActionDropDown
                          {...{
                            firstName,
                            profileImage,
                            email,
                            country,
                            city,
                            state,
                            closeActionDropdown,
                            setCustomModalDetails,
                          }}
                          dropdownItems={ACTION_DROPDOWN_ITEMS}
                        />
                      )}
                    </Box>
                  </ClickAwayListener>
                </Box>
                {width > 900 && (
                  <Box sx={styles.logoutContainer}>
                    {isLoggingOutUser ? (
                      <CircularProgress
                        size={15}
                        thickness={8}
                        sx={styles.logOutLoaderStyles}
                        aria-label="logging out"
                      />
                    ) : (
                      <>
                        <PowerSettingsNewIcon
                          sx={styles.logoutHeaderIcon}
                          onClick={() => removeToken()}
                        />
                      </>
                    )}
                  </Box>
                )}
                {width <= 900 && isLoggingOutUser && (
                  <Box sx={styles.logoutContainer}>
                    <CircularProgress
                      size={15}
                      thickness={8}
                      sx={styles.logOutLoaderStyles}
                      aria-label="logging out"
                    />
                  </Box>
                )}
              </Box>
            )}
            {onlyIncludeIcon && <Box sx={styles.itemContainer} />}
          </Box>
        ) : (
          <Box sx={styles.appBarContainer} maxWidth="xl">
            <Toolbar>
              <Typography
                component="img"
                sx={styles.logoStyles}
                src={LogoNova}
                alt="Novadontics"
                width={100}
                height={50}
                onClick={redirectToDefaultPage}
              />
            </Toolbar>
            {headerNonLoggedInActions().showheaderDetails && (
              <Box sx={styles.itemContainer}>
                {!!headerNonLoggedInActions().headingText && (
                  <Box sx={styles.headingTextBox}>
                    <Typography
                      sx={styles.headerText}
                      onClick={() =>
                        headerNonLoggedInActions().onHeadingClick()
                      }
                    >
                      {headerNonLoggedInActions().headingText}
                    </Typography>
                  </Box>
                )}
                <>
                  {headerNonLoggedInActions().buttonIcon === "homeIcon" ? (
                    <Box style={styles.headerNotLoginContainer}>
                      <HomeOutlinedIcon
                        sx={{
                          ...styles.clickableIcon,
                        }}
                        onClick={() =>
                          headerNonLoggedInActions().buttonClickHandler()
                        }
                      />
                      {!onlyIncludeIcon && <LanguageSelector />}
                    </Box>
                  ) : (
                    <>
                      {width > 900 ? (
                        <>
                          <Box
                            sx={{
                              ...styles.headingTextBox,
                              ...styles.middleBox,
                            }}
                          >
                            <Box sx={styles.btnContainer}>
                              <Typography
                                sx={styles.btnStyles}
                                onClick={() => redirectToPage(LOGIN)}
                              >
                                {t("LOGIN")}
                              </Typography>
                              <Typography sx={styles.slash}>/</Typography>
                              <Typography
                                sx={styles.btnStyles}
                                onClick={() => redirectToPage(SIGNUP)}
                              >
                                {t("SIGN_UP")}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              ...styles.btnContainer,
                              ...styles.headingTextBox,
                              ...styles.helpContainer,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={styles.btnStyles}
                                onClick={(event) => {
                                  setAnchorHelpPopover(event.currentTarget);
                                }}
                              >
                                {t("HELP")}
                              </Typography>
                              <HelpMenu
                                open={Boolean(anchorHelpPopover)}
                                anchorEl={anchorHelpPopover}
                                handleClose={() => {
                                  setAnchorHelpPopover(null);
                                }}
                                items={[
                                  {
                                    name: t("CONTACT_CARE_PULSE"),
                                    handleClose: () => {
                                      setAnchorHelpPopover(null);
                                      composeEmail(teamCarePulseEmail);
                                    },
                                  },
                                  {
                                    name: t("CARE_PULSE_BROCHURE"),
                                    handleClose: () => {
                                      setAnchorHelpPopover(null);
                                      setOpenBrochure(true);
                                    },
                                  },
                                ]}
                              />
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <ClickAwayListener onClickAway={closeActionDropdown}>
                          <Box>
                            <Box
                              sx={styles.menuIconBox}
                              onClick={toggleActionDropDown}
                            >
                              <MenuIcon sx={styles.menuIcon} />
                            </Box>
                            {showActionDropDown && (
                              <HeaderActionDropDown
                                {...{
                                  closeActionDropdown,
                                  setCustomModalDetails,
                                }}
                                dropdownItems={NON_LOGGED_IN_DROPDOWN}
                                openBrochure={() => {
                                  setOpenBrochure(true);
                                }}
                              />
                            )}
                          </Box>
                        </ClickAwayListener>
                      )}
                      {!onlyIncludeIcon && <LanguageSelector />}
                    </>
                  )}
                </>
              </Box>
            )}
          </Box>
        )}
      </AppBar>
      {customModalDetails.showModal && (
        <CustomPrompt
          open={customModalDetails.showModal}
          fullWidth
          showLoader={isLoggingOutUser}
          heading="Caution"
          modalIcon={warningIcon}
          modalIconAlt="warning"
          descriptionLineOne={customModalDetails.heading}
          descriptionLineTwo={customModalDetails.descriptionLine1}
          buttonOneText="Confirm"
          buttonTwoText="Cancel"
          onButtonOneClick={customModalDetails.confirmDeleteHandler}
          onButtonTwoClick={customModalDetails.closeModalHandler}
          closeModalHandler={customModalDetails.closeModalHandler}
        />
      )}
      <CustomNewModal
        openModal={openBrochure}
        fullWidth
        maxWidth="xl"
        closeModalHandler={() => {
          setOpenBrochure(false);
        }}
        includeCloseButton
      >
        <Box sx={styles.brochureContainer}>
          <Typography sx={styles.brochureHeadingStyle}>
            {t("CARE_PULSE_BROCHURE")}
          </Typography>
        </Box>
        <Typography
          component={"iframe"}
          src={
            i18n.language === "en"
              ? "https://contentdelivery-api.carepulse.app/public/carepulsedentistbrochure-EN-1783327945343.pdf"
              : "https://contentdelivery-api.carepulse.app/public/carepulsedentistbrochure-ES-1784008258182.pdf"
          }
          sx={styles.pdfStyle}
        ></Typography>
      </CustomNewModal>
      <LoggedInLanguagePopup
        open={showLanguagePreferencePopup}
        onClose={() => setShowLanguagePreferencePopup(false)}
      />
    </React.Fragment>
  );
};

export default Header;
